import { Detection, EntityFields, HeldFeedField } from 'src/data-capture/types';
import { entityFieldsToFilterOut } from '../../detected-entities/hooks/DetectionActionMutations';
import { fieldsToHide } from '../../merge-review/ReviewHelperFunctions';
import { ConflictState, DetectionState, EntityCategory, Field } from '../useDetectedEntitiesStore';
import { heldFeedFieldsByConflict } from './heldFeedFields';
import { isEmpty } from './isEmpty';

type ExtractionResult = {
  [category in EntityCategory]?: DetectionState;
};

export default function extractCategoriesForDetection(detectedEntity: Detection): ExtractionResult {
  const { detection, held: heldFeedFields } = holdBackFeedFields(detectedEntity);
  const filteredDetection = applyFieldFiltering(detection);

  // If no fields remain after filtering, return empty
  if (Object.keys(filteredDetection.entity.fields).length === 0) {
    return {};
  }

  const entityType = filteredDetection.entity.entityType;
  const metadata = filteredDetection.entity.metadata;

  // Get ordered, filtered field keys upfront
  const orderedFieldKeys = Object.keys(filteredDetection.entity.fields)
    .filter(key => !fieldsToHide.includes(key))
    .filter(key => {
      const value = filteredDetection.entity.fields[key];
      const isRequired = !metadata[key]?.isOptional;
      const hasValue = !isEmpty(value);
      return hasValue || isRequired;
    })
    .sort((a, b) => {
      return (
        (metadata[a]?.orderPosition ?? Number.MAX_SAFE_INTEGER) -
        (metadata[b]?.orderPosition ?? Number.MAX_SAFE_INTEGER)
      );
    });

  if (orderedFieldKeys.length === 0) {
    return {};
  }

  const shouldPreselectAllFields = detection.conflicts.length === 1 && detection.isConflictable;

  const createField = (key: string): Field => ({
    key,
    metadata: metadata[key],
    value: filteredDetection.entity.fields[key],
    isReadOnly:
      (typeof filteredDetection.entity.fields[key] === 'object' &&
        filteredDetection.entity.fields[key] !== null) ||
      (entityFieldsToFilterOut.fieldNames.includes(key) &&
        entityType === entityFieldsToFilterOut.entityType),
    isSelected: shouldPreselectAllFields,
    isResolved: false,
    isDiscarded: false,
    isActioning: false,
  });

  const createDetectionState = (overrides: Partial<DetectionState> = {}): DetectionState => {
    const base: DetectionState = {
      id: detection.id,
      metadata,
      entityId: detection.entity.id,
      entityType,
      entityLabel: detection.entityLabel,
      fieldsByKey: {},
      conflicts: detection.conflicts.map(c => ({
        id: c.conflictingEntityFields.id,
        conflictingEntityFields: c.conflictingEntityFields,
        conflictingFields: c.conflictingFields,
        existingOwners: c.conflictingEntityFields.owners,
      })),
      isResolved: false,
      isDiscarded: false,
      isActioning: false,
      lastAction: null,
      isConflictable: detection.isConflictable,
      resolvedOwners: detection.resolvedOwners,
      heldFeedFields,
    };

    return { ...base, ...overrides };
  };

  // Determine detection-level categories
  const shouldRenderAsGroup = filteredDetection.isConflictable;
  if (shouldRenderAsGroup) {
    const fieldsByKey = orderedFieldKeys.reduce(
      (acc, fieldName) => {
        acc[fieldName] = createField(fieldName);
        return acc;
      },
      {} as Record<string, Field>,
    );

    const detectionState = createDetectionState({
      fieldsByKey,
      selectedConflictIndex: shouldPreselectAllFields ? 0 : undefined,
    });

    const everyConflictIsIdentical = detectionState.conflicts.every(isConflictIdentical);

    if (detectionState.conflicts.length > 0 && everyConflictIsIdentical) {
      return {};
    }

    detectionState.conflicts = detectionState.conflicts.filter(
      conflict => !isConflictIdentical(conflict),
    );

    // Determine category based on conflict type:
    // - INFORMATION_UPDATE = unambiguous match (strict field like PRN matched) → 'info-update'
    // - CONFLICT = ambiguous, user must choose → 'conflicts'
    const hasOnlyInfoUpdates =
      filteredDetection.conflicts.length > 0 &&
      filteredDetection.conflicts.every(c => c.conflictType === 'INFORMATION_UPDATE');

    const category = getCategory(detectionState.conflicts.length, hasOnlyInfoUpdates);

    return {
      [category]: detectionState,
    };
  }

  // Split fields between 'new' and 'info-update' categories
  const conflict = filteredDetection.conflicts?.[0];
  const existingFields = Object.keys(conflict?.conflictingEntityFields ?? {});

  return orderedFieldKeys.reduce(
    (result, fieldName) => {
      const existingValue = conflict?.conflictingEntityFields[fieldName];
      const hasExistingValue = existingFields.includes(fieldName) && !isEmpty(existingValue);
      const categoryName: EntityCategory = hasExistingValue ? 'info-update' : 'new';

      if (!result[categoryName]) {
        result[categoryName] = createDetectionState();
      }

      result[categoryName].fieldsByKey[fieldName] = createField(fieldName);
      return result;
    },
    {} as { [category in EntityCategory]?: DetectionState },
  );
}

/**
 * A connected asset takes its identifiers and valuation from the data feed, so a document value
 * that would change one is held back from that asset and kept by asset so the review can show
 * both values; the update payload leaves it out for whichever asset is selected. It is only taken
 * off the review when every asset the detection could update holds it back.
 */
function holdBackFeedFields(detection: Detection): {
  detection: Detection;
  held: Record<string, HeldFeedField[]>;
} {
  const held = heldFeedFieldsByConflict(detection);
  const heldByEveryAsset = (field: string) =>
    detection.conflicts.length > 0 &&
    detection.conflicts.every(conflict =>
      held[String(conflict.conflictingEntityFields.id)]?.some(
        heldField => heldField.field === field,
      ),
    );
  const heldFields = Object.keys(detection.entity.fields).filter(heldByEveryAsset);
  if (heldFields.length === 0) {
    return { detection, held };
  }
  return {
    detection: {
      ...detection,
      entity: {
        ...detection.entity,
        fields: Object.fromEntries(
          Object.entries(detection.entity.fields).filter(([key]) => !heldFields.includes(key)),
        ),
      },
      conflicts: detection.conflicts.map(conflict => ({
        ...conflict,
        conflictingFields: conflict.conflictingFields.filter(field => !heldFields.includes(field)),
      })),
    },
    held,
  };
}

/**
 * For non-conflictable entities with conflicts, filter out fields that the backend
 * didn't flag as conflicting and that already exist on the existing entity.
 */
function applyFieldFiltering(detection: Detection): Detection {
  // If conflictable or no conflicts, return as-is
  if (detection.isConflictable || detection.conflicts.length === 0) {
    return detection;
  }

  const { entityFieldsWithNewValues, conflictingFieldsWithNewValues } =
    getEntityFieldsAndConflictsWithNewValues(detection);

  // If no fields have new values, this detection should be filtered out entirely
  if (Object.keys(entityFieldsWithNewValues).length === 0) {
    return {
      ...detection,
      entity: {
        ...detection.entity,
        fields: {},
      },
    };
  }

  return {
    ...detection,
    entity: {
      ...detection.entity,
      fields: entityFieldsWithNewValues,
    },
    conflicts: conflictingFieldsWithNewValues.length > 0 ? detection.conflicts : [],
  };
}

function getEntityFieldsAndConflictsWithNewValues(detection: Detection) {
  const conflict = detection.conflicts[0];
  const existingFields = conflict.conflictingEntityFields;

  return Object.keys(detection.entity.fields).reduce(
    (acc, fieldName) => {
      const entityValue = detection.entity.fields[fieldName];
      const isInConflictingFields = conflict.conflictingFields.includes(fieldName);
      const hasExistingValue = fieldName in existingFields && !isEmpty(existingFields[fieldName]);

      if (isInConflictingFields) {
        return {
          entityFieldsWithNewValues: {
            ...acc.entityFieldsWithNewValues,
            [fieldName]: entityValue,
          },
          conflictingFieldsWithNewValues: [...acc.conflictingFieldsWithNewValues, fieldName],
        };
      }

      // Trust backend: if field exists on existing entity and backend didn't flag it, skip it
      if (hasExistingValue) {
        return acc;
      }

      // Field doesn't exist on existing entity and has a value → new info
      if (!isEmpty(entityValue)) {
        return {
          ...acc,
          entityFieldsWithNewValues: {
            ...acc.entityFieldsWithNewValues,
            [fieldName]: entityValue,
          },
        };
      }

      return acc;
    },
    {
      entityFieldsWithNewValues: {} as EntityFields,
      conflictingFieldsWithNewValues: [] as string[],
    },
  );
}

function isConflictIdentical(conflict: ConflictState): boolean {
  return conflict.conflictingFields.length === 0;
}

function getCategory(conflictCount: number, hasOnlyInfoUpdates: boolean): EntityCategory {
  if (conflictCount === 0) {
    return 'new';
  }
  if (hasOnlyInfoUpdates) {
    return 'info-update';
  }
  return 'conflicts';
}
